<template>
  <div class="commercial-calendar">
    <div class="main-content">
      <div
        class="calendar-container"
        ref="dragWrap"
      >
        <vue-cal
          ref="vuecal"
          :key="calendarKey"
          class="vuecal-custom"
          :locale="locale"
          :events="filteredEvents"
          default-view="week"
          :disable-views="['years']"
          :time-from="0"
          :time-to="(24 * 60) - 1"
          :time-step="30"
          :snap-to-time="30"
          today-button
          events-on-month-view
          :editable-events="{ drag: true }"
          @event-click="onEventClick"
          :dblclick-to-navigate="false"
          @event-change="onEventChange"
          @view-change="onViewChange"
          @ready="onCalendarReady"
          @cell-click="() => {}"
          @cell-dblclick="() => {}"
          :week-starts-on="1"
        />
      </div>

      <div class="filters-container">
        <CalendarFilters
          :available-formats="availableFormats"
          :available-packagings="availablePackagings"
          :available-custodies="availableCustodies"
          :available-process-plants="availableProcessPlants"
          :format-filters="formatFilters"
          :packaging-filters="packagingFilters"
          :custody-filters="custodyFilters"
          :process-plant-filters="processPlantFilters"
          @toggle-format-filter="toggleFormatFilter"
          @toggle-packaging-filter="togglePackagingFilter"
          @toggle-custody-filter="toggleCustodyFilter"
          @toggle-process-plant-filter="toggleProcessPlantFilter"
        />

        <TonnageSummary
          :total-tonnage="totalTonnage"
          :event-count="visibleEventCount"
        />
      </div>
    </div>

    <ColorPickerModal
      :visible="showColorModal"
      :event="selectedEvent"
      :selected-color.sync="newColor"
      :default-color="defaultColorForEvent"
      @save="saveColor"
      @cancel="cancelColor"
      @reset-default="resetToDefaultColor"
    />

    <EventInfoModal
      :visible="showEventInfoModal"
      :event-info="eventInfo"
      :show-notes="shouldShowNotes"
      @close="closeEventInfoModal"
      @save="saveEventNotes"
      @update-notes="updateEventNotes"
    />
  </div>
</template>

<script>
/* eslint-disable */
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'
import axios from 'axios'
import CalendarLegend from './CalendarLegend.vue'
import CalendarFilters from './CalendarFilters.vue'
import TonnageSummary from './TonnageSummary.vue'
import ColorPickerModal from './ColorPickerModal.vue'
import EventInfoModal from './EventInfoModal.vue'
import useEventColors from '../composables/useEventColors.js'

// Global flag to track if this calendar instance has ever been loaded
let calendarHasBeenInitialized = false

export default {
  name: 'CommercialCalendar',
  components: {
    VueCal,
    CalendarLegend,
    CalendarFilters,
    TonnageSummary,
    ColorPickerModal,
    EventInfoModal,
  },

  data () {
    return {
      locale: 'es',
      events: [],
      calendarKey: 0,
      originalWeekdayLabels: null,
      originalWeekdayShortLabels: null,
      // Auto-navegación por bordes
      dragActive: false,
      edgeTimer: null,
      hoverTimer: null,
      hoverCooldown: false,
      edgeSide: null, // 'left' | 'right' | null
      isAttachingNavigation: false, // Flag to prevent concurrent executions

      // Filtros
      formatFilters: {
        entero: true,
        harinado: true,
        multiparticle: true,
        pelet_3: true,
        pelet_6: true,
        pelet_8: true,
        pelet_12: true
      },
      packagingFilters: { granel: true, ensacado: true, maxisaco: true },
      custodyFilters: {},
      processPlantFilters: {},

      // Modales
      showColorModal: false,
      showColorPickerModal: false,
      showEventInfoModal: false,
      selectedEvent: null,
      eventInfo: null,
      newColor: '#ffffff',

      // Control de calendario
      isInitialLoad: true,
      hasEverLoaded: false,
      customNavigationActive: false,
      navigationWrapperEl: null,
      prevDayButtonEl: null,
      nextDayButtonEl: null,

      // Para detectar doble click manualmente
      lastClickTime: 0,
      lastClickedEvent: null,

      // Para colores dinámicos
      dynamicColorClasses: new Set()
    }
  },

  setup() {
    const { getFormatColor, getPackagingBorderColor, getEventClass, ensureColorClass } = useEventColors()
    return { getFormatColor, getPackagingBorderColor, getEventClass, ensureColorClass }
  },

  mounted () {
    console.log('🚀 mounted() llamado')
    // Calculate current week date range
    const today = new Date()
    const day = today.getDay()
    const diff = today.getDate() - day + (day === 0 ? -6 : 1)
    const monday = new Date(today)
    monday.setDate(diff)
    const sunday = new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000)
    const startStr = monday.toISOString().split('T')[0]
    const endStr = sunday.toISOString().split('T')[0]

    console.log('📅 Cargando eventos para semana:', startStr, 'hasta', endStr)

    // Load events for current week
    this.loadEvents(startStr, endStr).then(() => {
      console.log('✅ loadEvents completado, eventos cargados:', this.events.length)
      console.log('📋 Eventos filtrados:', this.filteredEvents.length)
      // Force calendar re-render by incrementing key
      this.$nextTick(() => {
        console.log('🔄 Incrementando calendarKey para forzar re-render')
        this.calendarKey++
        // Re-setup navigation after re-render
        this.$nextTick(() => {
          setTimeout(() => {
            this.setupCustomNavigation()
          }, 150)
        })
      })
    })

    this.$nextTick(() => {
      this.setupNativeDoubleClickListener()
      this.setupCustomNavigation()
    })

    // Hover general (cuando NO estás arrastrando)
  },

  beforeDestroy () {
    window.removeEventListener('mousemove', this.onHoverEdge)
    window.removeEventListener('mouseleave', this.onLeaveEdge)
    window.removeEventListener('dragover', this.onGlobalDragOver)
    if (this.edgeTimer) clearInterval(this.edgeTimer)
    if (this.hoverTimer) clearTimeout(this.hoverTimer)
  },

  computed: {
    shouldShowNotes () {
      // En el calendario comercial, SIEMPRE mostrar las notas
      return true
    },

    filteredEvents () {
      return this.events.filter(event => {
        if (!this.formatFilters[event.format]) return false
        if (!this.packagingFilters[event.packaging]) return false
        const custody = event.company_from || event.company_to
        if (custody && !this.custodyFilters[custody]) return false
        if (event.process_plant && !this.processPlantFilters[event.process_plant]) return false
        return true
      })
    },

    defaultColorForEvent() {
      return this.selectedEvent ? this.getFormatColor(this.selectedEvent.format) : '#ffffff'
    },

    availableCustodies () {
      const visible = this.getVisibleEvents()
      const s = new Set()
      visible.forEach(e => { if (e.company_from) s.add(e.company_from); if (e.company_to) s.add(e.company_to) })
      return Array.from(s).sort()
    },

    availableProcessPlants () {
      const visible = this.getVisibleEvents()
      const s = new Set()
      visible.forEach(e => { if (e.process_plant) s.add(e.process_plant) })
      return Array.from(s).sort()
    },

    visibleAndFilteredEvents () {
      const visible = this.getVisibleEvents()
      return visible.filter(event => {
        if (!this.formatFilters[event.format]) return false
        if (!this.packagingFilters[event.packaging]) return false
        const custody = event.company_from || event.company_to
        if (custody && !this.custodyFilters[custody]) return false
        if (event.process_plant && !this.processPlantFilters[event.process_plant]) return false
        return true
      })
    },

    totalTonnage () {
      return this.visibleAndFilteredEvents.reduce((t, e) => t + (e.tonnage || 0), 0)
    },

    visibleEventCount () { return this.visibleAndFilteredEvents.length },

    availableFormats () {
      const visible = this.getVisibleEvents()
      const s = new Set()
      visible.forEach(e => { if (e.format) s.add(e.format) })
      return Array.from(s).sort()
    },

    availablePackagings () {
      const visible = this.getVisibleEvents()
      const s = new Set()
      visible.forEach(e => { if (e.packaging) s.add(e.packaging) })
      return Array.from(s).sort()
    }
  },

  watch: {
    availableFormats(n) { this.updateFormatFilters(n) },
    availablePackagings(n) { this.updatePackagingFilters(n) },
    availableCustodies() { this.updateCustodyFilters() },
    availableProcessPlants() { this.updateProcessPlantFilters() }
  },

  methods: {
    /* ====== helpers de tiempo ====== */
    parseToLocalDate(input) {
      if (!input) return null
      if (input instanceof Date) return input
      const s = String(input)
      if (/[zZ]|[+-]\d{2}:\d{2}$/.test(s)) return new Date(s)
      const [datePart, timePart = '00:00:00'] = s.trim().split(/[T ]/)
      const [y, m, d] = datePart.split('-').map(n => parseInt(n, 10))
      const [hh = 0, mm = 0, ss = 0] = timePart.split(':').map(n => parseInt(n, 10))
      return new Date(y, (m - 1), d, hh, mm, ss, 0)
    },
    endOfDay(date) { const d = new Date(date); d.setHours(23, 59, 59, 999); return d },
    toIsoWithOffsetLocal(dt) {
      const pad = n => `${Math.abs(n)}`.padStart(2, '0')
      const y = dt.getFullYear(), m = pad(dt.getMonth() + 1), d = pad(dt.getDate())
      const hh = pad(dt.getHours()), mm = pad(dt.getMinutes()), ss = pad(dt.getSeconds())
      const off = -dt.getTimezoneOffset(), sign = off >= 0 ? '+' : '-', oh = pad(Math.trunc(Math.abs(off)/60)), om = pad(Math.abs(off)%60)
      return `${y}-${m}-${d}T${hh}:${mm}:${ss}${sign}${oh}:${om}`
    },

    /* ====== filtros ====== */
    toggleFormatFilter({ format, checked }) { this.$set(this.formatFilters, format, checked) },
    togglePackagingFilter({ packaging, checked }) { this.$set(this.packagingFilters, packaging, checked) },
    toggleCustodyFilter({ custody, checked }) { this.$set(this.custodyFilters, custody, checked) },
    toggleProcessPlantFilter({ processPlant, checked }) { this.$set(this.processPlantFilters, processPlant, checked) },

    /* ====== calendario ====== */
    getVisibleEvents () {
      if (!this.$refs.vuecal || !this.$refs.vuecal.view) return this.events
      const { startDate, endDate } = this.$refs.vuecal.view
      return this.events.filter(e => {
        const d = new Date(e.start)
        return d >= startDate && d <= endDate
      })
    },

    onCalendarReady() {
      this.$nextTick(() => {
        this.updateDynamicFilters()
        setTimeout(() => { this.setupNativeDoubleClickListener() }, 100)
        // setTimeout(() => { this.attachNavigationListeners() }, 1000)
        // Only reset to today's date on the very first load, never on refreshes
        if (!calendarHasBeenInitialized) {
          calendarHasBeenInitialized = true
          setTimeout(() => { this.resetWeekViewTo(new Date()) }, 100)
        }
      })
    },

    onViewChange({ startDate, endDate, view }) {
      let vuecal = this.$refs.vuecal
      console.log('LAVUECAL:', vuecal)
      if (this.isInitialLoad) {
        this.isInitialLoad = false
        return
      }

      // Handle both string and Date object formats
      const start = typeof startDate === 'string'
        ? startDate.split('T')[0]
        : startDate.toISOString().split('T')[0]
      const end = typeof endDate === 'string'
        ? endDate.split('T')[0]
        : endDate.toISOString().split('T')[0]

      this.loadEvents(start, end)

      // Ensure we pass a Date object to updateWeekdayLabels
      const startDateObj = typeof startDate === 'string' ? new Date(startDate) : startDate
      console.log('📅 onViewChange - nueva semana desde', start, 'hasta', end, "|||", startDateObj)
      this.updateWeekdayLabels(startDateObj)
      vuecal.$forceUpdate()

      this.$nextTick(() => {
        this.updateDynamicFilters()
        setTimeout(() => {
          this.attachDoubleClickToEvents()
          this.attachNavigationListeners()
        }, 100)
      })
    },

    async onEventChange({ event }) {
      const dt = new Date(event.start)
      try {
        await axios.put(
          `/api/v1/calendar_events/${event.order_id}`,
          { delivery_date: this.toIsoWithOffsetLocal(dt) },
          { headers: this.csrfHeaders() }
        )
        const i = this.events.findIndex(e => e.order_id === event.order_id)
        if (i !== -1) {
          this.events[i].start = new Date(event.start)
          this.events[i].end = new Date(event.end)
        }
      } catch (err) { console.error('Error al mover evento:', err); this.loadEvents() }
    },

    onEventClick(event, e) {
      const currentTime = new Date().getTime()
      const timeDiff = currentTime - this.lastClickTime

      if (timeDiff < 400 && this.lastClickedEvent) {
        const sameEvent = (this.lastClickedEvent.id === (event.id || event.order_id) ||
                           this.lastClickedEvent.order_id === (event.id || event.order_id))
        if (sameEvent) {
          this.showEventInfo(event)
          this.lastClickTime = 0
          this.lastClickedEvent = null
          return false
        }
      }

      this.lastClickTime = currentTime
      this.lastClickedEvent = event

      return false
    },

    // Modales
    closeEventInfoModal() { this.showEventInfoModal = false; this.eventInfo = null },
    updateEventNotes(notes) { if (this.eventInfo) this.eventInfo.notes = notes },

    // Configurar listener de doble click nativo como respaldo
    setupNativeDoubleClickListener() {
      const observer = new MutationObserver(() => { this.attachDoubleClickToEvents() })
      const calendarContainer = this.$refs.vuecal?.$el
      if (calendarContainer) {
        observer.observe(calendarContainer, { childList: true, subtree: true })
        this.attachDoubleClickToEvents()
      }
    },

    attachDoubleClickToEvents() {
      const eventElements = document.querySelectorAll('.vuecal__event')
      eventElements.forEach(eventEl => {
        eventEl.removeEventListener('dblclick', this.handleNativeDoubleClick)
        eventEl.addEventListener('dblclick', this.handleNativeDoubleClick)
      })
    },

    handleNativeDoubleClick(nativeEvent) {
      nativeEvent.preventDefault()
      nativeEvent.stopPropagation()
      const wrapper = nativeEvent.target.closest('[data-order-id]')
      if (wrapper) {
        const eventId = wrapper.getAttribute('data-order-id')
        const found = this.events.find(e => String(e.order_id || e.id) === String(eventId))
        if (found) {
          this.showEventInfo(found)
          return
        }
      }
      if (this.lastClickedEvent) {
        this.showEventInfo(this.lastClickedEvent)
      }
    },

    async showEventInfo(event) {
      if (!event) {
        console.error('No se recibió el objeto evento válido')
        return
      }

      this.eventInfo = {
        id: event.order_id || event.id,
        empresa_cliente: event.billing_name || event.company_to || 'Sin cliente',
        custodia: event.company_from || 'Sin custodia',
        envase: this.getPackagingLabel(event.packaging),
        formato: this.getFormatLabel(event.format),
        peso_estimado: event.tonnage ? `${event.tonnage.toFixed(1)} toneladas` : 'No especificado',
        planta_proceso: this.getProcessPlantLabel(event.process_plant),
        notes: event.notes || '',
        status: event.status, // Agregar el status para controlar la visibilidad de notas
        ingredients: []
      }

      this.showEventInfoModal = true

      try {
        const orderId = event.order_id || event.id
        const { data } = await axios.get(`/api/v1/calendar_events/${orderId}/ingredients`, { headers: this.csrfHeaders() })
        this.eventInfo.ingredients = data.ingredients || []
      } catch (error) {
        console.error('Error al cargar ingredientes:', error)
        this.eventInfo.ingredients = []
      }
    },

    handleEventInfoSave() { this.saveEventNotes(); this.showEventInfoModal = false },
    handleEventInfoClose() { this.showEventInfoModal = false; this.eventInfo = null },
    handleUpdateNotes(notes) { if (this.eventInfo) this.eventInfo.notes = notes },

    openColorPicker (calendarEvent) {
      this.selectedEvent = calendarEvent
      this.newColor = calendarEvent.color_hex || this.getFormatColor(calendarEvent.format)
      this.showColorModal = true
    },

    handleColorPickerSave(color) {
      if (!this.selectedEvent) return
      this.selectedEvent.customColor = color
      this.saveColor()
      this.showColorPickerModal = false
    },

    handleColorPickerCancel() { this.showColorPickerModal = false; this.selectedEvent = null },

    resetToDefaultColor () { if (this.selectedEvent) this.newColor = this.getFormatColor(this.selectedEvent.format) },

    // Nuevo y Modificada se ven grises. El color de formato aparece desde En proceso.
    isGrayStatus (status) {
      return status === 'new' || status === 'modified'
    },

    eventClassFor (event) {
      const baseClass = this.isGrayStatus(event.status)
        ? 'status-new'
        : (event.color_hex ? this.ensureColorClass(event.color_hex, this.dynamicColorClasses) : this.getEventClass(event.format, event.packaging))
      const hasNotesClass = event.notes && event.notes.trim() ? 'has-notes' : ''
      const statusClass = `status-${event.status}`

      return [baseClass, hasNotesClass, statusClass].filter(Boolean).join(' ')
    },

    async loadEvents (startDate = null, endDate = null) {
      console.log('📥 loadEvents llamado con:', { startDate, endDate })
      const params = {}
      if (startDate) params.start_date = startDate
      if (endDate) params.end_date = endDate

      try {
        const { data } = await axios.get('/api/v1/calendar_events', { params, headers: this.csrfHeaders() })
        console.log('📦 Datos recibidos del servidor:', data.length, 'eventos')

        this.events = data.map(ev => {
          const eventClass = this.eventClassFor(ev)

          const start = this.parseToLocalDate(ev.start || ev.delivery_date)
          const computedEnd = this.parseToLocalDate(ev.end) || new Date(start.getTime() + 60 * 60 * 1000)
          const endSameDay = (computedEnd.getFullYear() === start.getFullYear()
            && computedEnd.getMonth() === start.getMonth()
            && computedEnd.getDate() === start.getDate())
            ? computedEnd
            : this.endOfDay(start)

          // Formato personalizado del título con alineación
          const orderId = ev.order_id || ev.id || 'N/A'
          const weight = ev.tonnage ? `${ev.tonnage.toFixed(1)}t` : ''
          const clientName = ev.billing_name || ev.company_to || 'Sin cliente'

          const customTitle = `<div class=\"event-wrapper\" data-order-id=\"${orderId}\"><div class=\"event-line-1\"><span class=\"event-id\">${orderId}</span><span class=\"event-weight\">${weight}</span></div><div class=\"event-line-2\"><span class=\"event-client\">${clientName}</span></div></div>`

          return {
            ...ev,
            editable: true,
            start,
            end: endSameDay,
            class: eventClass,
            title: customTitle
          }
        })

        console.log('🎨 Eventos mapeados:', this.events.length)
        console.log('📋 Antes de updateDynamicFilters - filteredEvents:', this.filteredEvents.length)

        // Update filters immediately after loading events
        this.updateDynamicFilters()

        console.log('📋 Después de updateDynamicFilters - filteredEvents:', this.filteredEvents.length)

        this.$nextTick(() => {
          setTimeout(() => { this.attachDoubleClickToEvents() }, 100)
        })
      } catch (error) {
        console.error('Error cargando eventos:', error)
      }
    },

    async saveEventNotes() {
      if (!this.eventInfo) return

      // Store the notes before the modal closes
      const orderId = this.eventInfo.id
      const notesToSave = this.eventInfo.notes

      try {
        await axios.put(`/api/v1/calendar_events/${orderId}`, { notes: notesToSave }, { headers: this.csrfHeaders() })

        const i = this.events.findIndex(e => e.order_id === orderId || e.id === orderId)
        if (i !== -1) {
          const event = this.events[i]
          event.notes = notesToSave
          event.class = this.eventClassFor(event)
        }

        this.refreshCalendar()

        // Close the modal after successfully saving
        this.closeEventInfoModal()
      } catch (e) {
        console.error('Error al guardar notas:', e)
        // Don't close modal if there was an error
      }
    },

    async sendColorUpdate(orderId, colorHex) {
      return axios.put(`/api/v1/calendar_events/${orderId}`, { color_hex: colorHex }, { headers: this.csrfHeaders() })
    },

    applyColorUpdateLocally(orderId, custom) {
      const i = this.events.findIndex(e => e.order_id === orderId)
      if (i !== -1) {
        const event = this.events[i]
        event.color_hex = custom ? this.newColor : null
        event.class = this.eventClassFor(event)
      }
    },

    async saveColor() {
      if (!this.selectedEvent) return
      const { order_id: orderId, format } = this.selectedEvent
      const defCol = this.getFormatColor(format)
      const custom = this.newColor !== defCol
      try {
        await this.sendColorUpdate(orderId, custom ? this.newColor : null)
        this.applyColorUpdateLocally(orderId, custom)
        this.refreshCalendar()
      } catch (err) { console.error('No se pudo guardar color:', err) }
      this.cancelColor()
    },

    refreshCalendar() {
      if (this.$refs.vuecal && typeof this.$refs.vuecal.updateEvents === 'function') {
        this.$refs.vuecal.updateEvents(this.filteredEvents)
      } else {
        // Save current view before re-rendering
        const currentView = this.$refs.vuecal?.view
        const currentStartDate = currentView?.startDate
        const currentEndDate = currentView?.endDate

        this.calendarKey++

        // Restore view after re-render if we had a current view
        if (currentStartDate && currentEndDate) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.restoreExactView(new Date(currentStartDate), new Date(currentEndDate))
              // Re-setup custom navigation after restoring view
              setTimeout(() => {
                this.setupCustomNavigation()
              }, 100)
            }, 100)
          })
        } else {
          // Even if no current view, setup navigation
          this.$nextTick(() => {
            setTimeout(() => {
              this.setupCustomNavigation()
            }, 100)
          })
        }
      }
    },

    cancelColor () { this.showColorModal = false; this.selectedEvent = null },

    csrfHeaders () {
      return {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Content-Type': 'application/json'
      }
    },

    // Labels
    getEventClass(format, packaging) {
      const f = format || 'other'
      const p = packaging || 'other'
      return `format-${f} packaging-${p}`
    },
    getPackagingLabel (p) {
      switch (p) {
        case 'granel': return 'Granel'
        case 'ensacado': return 'Ensacado'
        case 'maxisaco': return 'Maxisaco'
        default: return 'Otro'
      }
    },
    getFormatLabel (f) {
      switch (f) {
        case 'entero': return 'Entero'
        case 'harinado': return 'Harinado'
        case 'multiparticle': return 'Multipartícula'
        case 'pelet_3': return 'Pelet 3'
        case 'pelet_6': return 'Pelet 6'
        case 'pelet_8': return 'Pelet 8'
        case 'pelet_12': return 'Pelet 12'
        default: return 'Otro'
      }
    },
    getProcessPlantLabel (p) {
      switch (p) {
        case 'line1': return 'Línea 1'
        case 'line2': return 'Línea 2'
        case 'reception': return 'Recepción'
        default: return 'No especificado'
      }
    },

    goToShipments() { window.location.href = '/admin/orders' },

    updateFormatFilters(newFormats) {
      const fresh = {}
      newFormats.forEach(f => { fresh[f] = this.formatFilters[f] !== false })
      Object.keys(this.formatFilters).forEach(k => { if (!fresh.hasOwnProperty(k)) this.$delete(this.formatFilters, k) })
      Object.keys(fresh).forEach(k => { this.$set(this.formatFilters, k, fresh[k]) })
    },
    updatePackagingFilters(newPackagings) {
      const fresh = {}
      newPackagings.forEach(p => { fresh[p] = this.packagingFilters[p] !== false })
      Object.keys(this.packagingFilters).forEach(k => { if (!fresh.hasOwnProperty(k)) this.$delete(this.packagingFilters, k) })
      Object.keys(fresh).forEach(k => { this.$set(this.packagingFilters, k, fresh[k]) })
    },
    updateDynamicFilters () {
      const nf = {}
      this.availableFormats.forEach(f => { nf[f] = this.formatFilters[f] !== false })
      Object.keys(this.formatFilters).forEach(k => { if (!nf.hasOwnProperty(k)) this.$delete(this.formatFilters, k) })
      Object.keys(nf).forEach(k => { this.$set(this.formatFilters, k, nf[k]) })

      const np = {}
      this.availablePackagings.forEach(p => { np[p] = this.packagingFilters[p] !== false })
      Object.keys(this.packagingFilters).forEach(k => { if (!np.hasOwnProperty(k)) this.$delete(this.packagingFilters, k) })
      Object.keys(np).forEach(k => { this.$set(this.packagingFilters, k, np[k]) })

      this.updateCustodyFilters()
      this.updateProcessPlantFilters()
    },
    updateCustodyFilters () {
      const f = {}
      this.availableCustodies.forEach(c => { f[c] = this.custodyFilters[c] !== false })
      Object.keys(this.custodyFilters).forEach(k => { if (!f.hasOwnProperty(k)) this.$delete(this.custodyFilters, k) })
      Object.keys(f).forEach(k => { this.$set(this.custodyFilters, k, f[k]) })
    },
    updateProcessPlantFilters () {
      const f = {}
      this.availableProcessPlants.forEach(p => { f[p] = this.processPlantFilters[p] !== false })
      Object.keys(this.processPlantFilters).forEach(k => { if (!f.hasOwnProperty(k)) this.$delete(this.processPlantFilters, k) })
      Object.keys(f).forEach(k => { this.$set(this.processPlantFilters, k, f[k]) })
    },

    // -------- Navegación personalizada por flechas del header --------
    setupCustomNavigation() {
      this.$nextTick(() => {
        setTimeout(() => { this.attachNavigationListeners() }, 1)
      })
    },

    attachNavigationListeners() {
      // Prevent concurrent executions
      if (this.isAttachingNavigation) {
        return
      }

      this.isAttachingNavigation = true

      const calendarEl = this.$refs.vuecal?.$el
      if (!calendarEl) {
        this.isAttachingNavigation = false
        setTimeout(() => this.attachNavigationListeners(), 100)
        return
      }

      // Detect week navigation arrows (not day navigation arrows)
      // We need to be specific to avoid capturing day nav buttons
      const allPrevButtons = Array.from(calendarEl.querySelectorAll('.vuecal__arrow.vuecal__arrow--prev'))
      const allNextButtons = Array.from(calendarEl.querySelectorAll('.vuecal__arrow.vuecal__arrow--next'))

      // Filter to get only week navigation arrows (not day nav which have calendar-day-nav__btn class)
      const prevWeekButtons = allPrevButtons.filter(btn => !btn.classList.contains('calendar-day-nav__btn'))
      const nextWeekButtons = allNextButtons.filter(btn => !btn.classList.contains('calendar-day-nav__btn'))

      const prevButton = prevWeekButtons[0] || null
      const nextButton = nextWeekButtons[0] || null

      // Remove any duplicate week navigation arrows
      if (prevWeekButtons.length > 1) prevWeekButtons.slice(1).forEach(btn => btn.remove())
      if (nextWeekButtons.length > 1) nextWeekButtons.slice(1).forEach(btn => btn.remove())
      const todayButton = calendarEl.querySelector('.vuecal__today-btn')

      let navigationWrapper = this.navigationWrapperEl
      if (!navigationWrapper || !navigationWrapper.isConnected) {
        navigationWrapper = prevButton?.parentElement || nextButton?.parentElement || navigationWrapper
        if (navigationWrapper) this.navigationWrapperEl = navigationWrapper
      } else {
        navigationWrapper = this.navigationWrapperEl
      }

      if (!navigationWrapper) {
        this.isAttachingNavigation = false
        setTimeout(() => this.attachNavigationListeners(), 100)
        return
      }

      if (prevButton) {
        prevButton.removeEventListener('click', this.handlePrevNavigation, true)
        prevButton.addEventListener('click', this.handlePrevNavigation, true)
        prevButton.classList.add('calendar-week-nav__btn')
        prevButton.setAttribute('aria-label', 'Semana anterior')
        prevButton.setAttribute('title', 'Semana anterior')
        prevButton.textContent = '<<'
      }

      if (nextButton) {
        nextButton.removeEventListener('click', this.handleNextNavigation, true)
        nextButton.addEventListener('click', this.handleNextNavigation, true)
        nextButton.classList.add('calendar-week-nav__btn')
        nextButton.setAttribute('aria-label', 'Semana siguiente')
        nextButton.setAttribute('title', 'Semana siguiente')
        nextButton.textContent = '>>'
      }

      if (todayButton) {
        todayButton.removeEventListener('click', this.handleTodayNavigation, true)
        todayButton.addEventListener('click', this.handleTodayNavigation, true)
        if (todayButton.parentElement === navigationWrapper ||
            todayButton.parentElement?.classList?.contains('calendar-nav-wrapper')) {
          todayButton.classList.add('calendar-nav__today')
        } else {
          todayButton.classList.remove('calendar-nav__today')
        }
      }

      if (navigationWrapper) {
        navigationWrapper.classList.add('calendar-nav-wrapper')

        // Remove unexpected duplicated day navigation buttons but keep the stored references
        const existingPrevDayButtons = Array.from(navigationWrapper.querySelectorAll('.calendar-day-nav__prev'))
          .filter(btn => btn !== this.prevDayButtonEl)
        const existingNextDayButtons = Array.from(navigationWrapper.querySelectorAll('.calendar-day-nav__next'))
          .filter(btn => btn !== this.nextDayButtonEl)
        existingPrevDayButtons.forEach(btn => btn.remove())
        existingNextDayButtons.forEach(btn => btn.remove())

        if (!this.prevDayButtonEl) {
          this.prevDayButtonEl = document.createElement('button')
        }
        if (!this.nextDayButtonEl) {
          this.nextDayButtonEl = document.createElement('button')
        }

        const prevDayButton = this.prevDayButtonEl
        prevDayButton.type = 'button'
        prevDayButton.className = 'vuecal__arrow calendar-day-nav__btn calendar-day-nav__prev'
        prevDayButton.textContent = '<'
        prevDayButton.setAttribute('aria-label', 'Retroceder un día')
        prevDayButton.setAttribute('title', 'Retroceder un día')
        prevDayButton.removeEventListener('click', this.handlePrevDayNavigation, true)
        prevDayButton.addEventListener('click', this.handlePrevDayNavigation, true)

        const nextDayButton = this.nextDayButtonEl
        nextDayButton.type = 'button'
        nextDayButton.className = 'vuecal__arrow calendar-day-nav__btn calendar-day-nav__next'
        nextDayButton.textContent = '>'
        nextDayButton.setAttribute('aria-label', 'Avanzar un día')
        nextDayButton.setAttribute('title', 'Avanzar un día')
        nextDayButton.removeEventListener('click', this.handleNextDayNavigation, true)
        nextDayButton.addEventListener('click', this.handleNextDayNavigation, true)

        let leftGroup = navigationWrapper.querySelector('.calendar-nav-group--left')
        if (!leftGroup) {
          leftGroup = document.createElement('div')
          leftGroup.className = 'calendar-nav-group calendar-nav-group--left'
          navigationWrapper.insertBefore(leftGroup, navigationWrapper.firstChild)
        }

        // Only append buttons if they're not already in the correct group
        if (prevButton && prevButton.parentElement !== leftGroup) {
          leftGroup.appendChild(prevButton)
        }
        if (prevDayButton && prevDayButton.parentElement !== leftGroup) {
          leftGroup.appendChild(prevDayButton)
        }

        let rightGroup = navigationWrapper.querySelector('.calendar-nav-group--right')
        if (!rightGroup) {
          rightGroup = document.createElement('div')
          rightGroup.className = 'calendar-nav-group calendar-nav-group--right'
          navigationWrapper.appendChild(rightGroup)
        }

        // Only append buttons if they're not already in the correct group
        if (nextDayButton && nextDayButton.parentElement !== rightGroup) {
          rightGroup.appendChild(nextDayButton)
        }
        if (nextButton && nextButton.parentElement !== rightGroup) {
          rightGroup.appendChild(nextButton)
        }
      }

      // Release the lock
      this.isAttachingNavigation = false
    },

    handlePrevNavigation(event) {
      event.preventDefault()
      event.stopPropagation()
      this.allowDefaultNavigation('prev')
    },

    handleNextNavigation(event) {
      event.preventDefault()
      event.stopPropagation()
      this.allowDefaultNavigation('next')
    },

    handlePrevDayNavigation(event) {
      event.preventDefault()
      event.stopPropagation()
      this.navigateByDays(-1)
    },

    handleNextDayNavigation(event) {
      event.preventDefault()
      event.stopPropagation()
      this.navigateByDays(1)
    },

    handleTodayNavigation(event) {
      event.preventDefault()
      event.stopPropagation()
      console.log("Navegando a hoy", new Date())
      this.resetWeekViewTo(new Date())
    },

    resetWeekViewTo(date) {
      const vuecal = this.$refs.vuecal
      if (!vuecal) return
      const monday = this.getMondayOfWeek(date)
      console.log("Reseteando vista semanal a", monday)
      const endDate = new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000)
      if (vuecal.updateMutableDate) vuecal.updateMutableDate(monday)
      if (!vuecal.view || vuecal.view.id !== 'week') vuecal.switchView('week')
      if (vuecal.view) {
        vuecal.view.startDate = monday
        vuecal.view.endDate = endDate
        vuecal.$forceUpdate()
      }
      this.loadEventsForDateRange(monday, endDate)
      this.updateWeekdayLabels(monday)
    },

    restoreExactView(startDate, endDate) {
      const vuecal = this.$refs.vuecal
      if (!vuecal) return
      if (vuecal.updateMutableDate) vuecal.updateMutableDate(startDate)
      if (!vuecal.view || vuecal.view.id !== 'week') vuecal.switchView('week')
      if (vuecal.view) {
        vuecal.view.startDate = startDate
        vuecal.view.endDate = endDate
        vuecal.$forceUpdate()
      }
      this.loadEventsForDateRange(startDate, endDate)
      this.updateWeekdayLabels(startDate)
    },

    navigateByDays(direction) {
      if (!this.$refs.vuecal || !this.$refs.vuecal.view) return

      // Get the current start date, ensuring it's a proper Date object
      const currentStartDate = new Date(this.$refs.vuecal.view.startDate)

      // Calculate new start date by adding/subtracting days
      const newStartDate = new Date(currentStartDate)
      newStartDate.setDate(currentStartDate.getDate() + direction)

      // Calculate end date (6 days after start)
      const newEndDate = new Date(newStartDate)
      newEndDate.setDate(newStartDate.getDate() + 6)

      // Update VueCal's internal state
      if (this.$refs.vuecal.updateMutableDate) {
        this.$refs.vuecal.updateMutableDate(newStartDate)
      }

      // Update view dates
      this.$refs.vuecal.view.startDate = newStartDate
      this.$refs.vuecal.view.endDate = newEndDate
      this.$refs.vuecal.view.selectedDate = new Date(newStartDate)

      // Force calendar to re-render

      // Load events for the new date range
      this.loadEventsForDateRange(newStartDate, newEndDate)

      // Update weekday labels for the new week
      this.updateWeekdayLabels(newStartDate)

      this.$refs.vuecal.$forceUpdate()
      console.log('Navegación por días:', {
        direction,
        currentStartDate: currentStartDate.toISOString().split('T')[0],
        newStartDate: newStartDate.toISOString().split('T')[0],
        newEndDate: newEndDate.toISOString().split('T')[0]
      })
    },

    allowDefaultNavigation(direction) {
      if (direction === 'prev') this.$refs.vuecal.previous()
      else if (direction === 'next') this.$refs.vuecal.next()
    },

    getMondayOfWeek(date) {
      const d = new Date(date)
      const day = d.getDay()
      const diff = d.getDate() - day + (day === 0 ? -6 : 1)
      return new Date(d.setDate(diff))
    },

    loadEventsForDateRange(startDate, endDate) {
      const start = startDate.toISOString().split('T')[0]
      const end = endDate.toISOString().split('T')[0]
      this.loadEvents(start, end)
    },

    updateWeekdayLabels(startDate) {
      const vuecal = this.$refs.vuecal
      if (!vuecal?.texts) return

      this.captureOriginalWeekdayLabels()

      const currentViewId = vuecal.view?.id
      if (currentViewId === 'day') {
        this.restoreOriginalWeekdayLabels()
        return
      }
      if (currentViewId && currentViewId !== 'week') return

      const parsedBase = this.parseToLocalDate(startDate)
      if (!parsedBase || Number.isNaN(parsedBase.getTime())) return
      const baseDate = new Date(parsedBase.getTime())
      baseDate.setHours(0, 0, 0, 0)

      const localeTag = typeof this.locale === 'string' ? this.locale : 'es'
      const fullFormatter = new Intl.DateTimeFormat(localeTag, { weekday: 'long' })
      const shortFormatter = new Intl.DateTimeFormat(localeTag, { weekday: 'short' })

      const nextFullLabels = []
      const nextShortLabels = []

      for (let i = 0; i < 7; i += 1) {
        const date = new Date(baseDate.getTime())
        date.setDate(baseDate.getDate() + i)
        const full = this.capitalizeLabel(fullFormatter.format(date))
        const shortRaw = shortFormatter.format(date).replace(/\.$/, '')
        nextFullLabels.push(full)
        nextShortLabels.push(this.capitalizeLabel(shortRaw))
      }

      vuecal.texts.weekDays = nextFullLabels
      vuecal.texts.weekDaysShort = nextShortLabels

      const formatterWeekdays = this.originalWeekdayLabels ? [...this.originalWeekdayLabels] : [...nextFullLabels]
      const formatterWeekdaysShort = this.originalWeekdayShortLabels
        ? [...this.originalWeekdayShortLabels]
        : [...nextShortLabels]

      if (vuecal.utils?.date?.updateTexts) {
        const formattingTexts = { ...vuecal.texts }
        formattingTexts.weekDays = formatterWeekdays
        formattingTexts.weekDaysShort = formatterWeekdaysShort
        vuecal.utils.date.updateTexts(formattingTexts)
      }

      vuecal.$forceUpdate()
    },

    captureOriginalWeekdayLabels() {
      const vuecal = this.$refs.vuecal
      if (!vuecal?.texts) return
      if (!this.originalWeekdayLabels) this.originalWeekdayLabels = [...vuecal.texts.weekDays]
      if (!this.originalWeekdayShortLabels) {
        const shorts = vuecal.texts.weekDaysShort || []
        this.originalWeekdayShortLabels = [...shorts]
      }
    },

    restoreOriginalWeekdayLabels() {
      const vuecal = this.$refs.vuecal
      if (!vuecal?.texts) return

      if (this.originalWeekdayLabels?.length) {
        vuecal.texts.weekDays = [...this.originalWeekdayLabels]
      }
      if (this.originalWeekdayShortLabels?.length) {
        vuecal.texts.weekDaysShort = [...this.originalWeekdayShortLabels]
      }

      if (vuecal.utils?.date?.updateTexts) {
        const formattingTexts = { ...vuecal.texts }
        if (this.originalWeekdayLabels?.length) {
          formattingTexts.weekDays = [...this.originalWeekdayLabels]
        }
        if (this.originalWeekdayShortLabels?.length) {
          formattingTexts.weekDaysShort = [...this.originalWeekdayShortLabels]
        }
        vuecal.utils.date.updateTexts(formattingTexts)
      }

      vuecal.$forceUpdate()
    },

    capitalizeLabel(label) {
      if (!label) return ''
      return label.charAt(0).toUpperCase() + label.slice(1)
    },


  }
}
</script>

<style scoped>


.calendar-wrap { position: relative; }
/* ---------- contenedor y leyenda ---------- */
.commercial-calendar{padding:20px;max-width:auto;margin:0 auto;background:#f0f0f0;}
.calendar-header{text-align:center;margin-bottom:20px;background:#fff;padding:20px;border:1px solid #d3d8dc;border-radius:0;}
.calendar-header h2{color:#4e4e51;margin:0;font-size:2rem;}
.calendar-header p{color:#7f8c8d;margin:5px 0 15px;}

.color-legend{display:flex;flex-direction:column;align-items:center;gap:15px;margin-bottom:20px;}
.legend-section{text-align:center;}
.legend-section h4{margin:0 0 8px 0;color:#2c3e50;font-size:14px;}
.legend-row{display:flex;justify-content:center;gap:15px;}
.legend-item{display:flex;align-items:center;gap:6px;font-weight:500;font-size:12px;color:#2c3e50;}
.legend-color{width:16px;height:16px;border-radius:3px;border:1px solid #ddd;}

/* Colores de formato (fondo) */
.legend-color.format-entero       {background:#56B686;}
.legend-color.format-harinado     {background:#D85796;}
.legend-color.format-multiparticle{background:#3076EE;}
.legend-color.format-pelet_3      {background:#E31431;}
.legend-color.format-pelet_6      {background:#E87B37;}
.legend-color.format-pelet_8      {background:#845FEF;}
.legend-color.format-pelet_12     {background:#EBA23D;}

/* Colores de empaque (borde) */
.legend-color.packaging-granel   {background:#ffffff;border-left:8px solid #020405;}
.legend-color.packaging-ensacado {background:#ffffff;border-left:8px solid #4CE51E;}
.legend-color.packaging-maxisaco {background:#ffffff;border-left:8px solid #0CEEE7;}

/* ---------- layout principal ---------- */
.main-content{display:flex;gap:20px;margin-top:20px; margin-left: -30px; margin-right: -30px;}
.calendar-container{flex:1;background:#fff;border-radius:0;border:1px solid #d3d8dc;box-shadow:none;padding:12px;}
.filters-container{flex:0 0 280px;position:sticky;top:20px;align-self:flex-start}

/* ---------- responsive ---------- */
@media (max-width: 1024px) {
  .main-content{flex-direction:column-reverse;}
  .filters-container{flex:0 0 auto;margin-bottom:20px;position:static;width:100%;}
  .filters-section{position:static;width:100%;}
  .tonnage-summary{position:static;width:100%;}
}

/* ---------- filtros ---------- */
.filters-container{margin:0;}
.filters-section{background:#fff;border:1px solid #d3d8dc;box-shadow:none;border-radius:0;padding:20px;position:static;width:270px;}
.tonnage-summary{position:sticky;top:20px;z-index:20;margin-top:1rem}
.filters-section h4{margin:0 0 15px 0;color:#4e4e51;font-size:1em;font-weight:bold;text-transform:uppercase;}
.filter-group{margin-bottom:20px;}
.filter-group:last-child{margin-bottom:0;}
.filter-group h5{margin:0 0 10px 0;color:#95a5a6;font-size:13px;font-weight:normal;text-transform:uppercase;font-family:'Source Sans Pro',sans-serif;}
.filter-options{display:flex;flex-direction:column;gap:8px;}
.filter-option{display:flex;align-items:center;gap:8px;font-size:13px;color:#4e4e51;cursor:pointer;padding:7px 0;font-family:'Source Sans Pro',sans-serif;}
.filter-option input[type="checkbox"]{margin:0;width:16px;height:16px;}
.filter-option:hover{color:#4e4e51;}

/* ---------- eventos ---------- */
.vuecal-custom :deep(.vuecal__event){
  position: absolute !important;
  font-size:10px;
  font-weight:500;
  color:#fff !important;
  cursor:pointer;
  margin:1px;
  border-radius:3px;
  padding:2px 4px;
  line-height:1.1;
  box-shadow:0 1px 3px rgba(0,0,0,0.12);
  border:1px solid #ddd;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

/* Triángulo rojo para eventos con notas - esquina superior derecha */
.vuecal-custom :deep(.vuecal__event.has-notes)::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 8px 8px 0;
  border-color: transparent #ffee00 transparent transparent;
  z-index: 10;
}

/* ensure inner content does not override background */
.vuecal-custom :deep(.vuecal__event .vuecal__event-content),
.vuecal-custom :deep(.vuecal__event .vuecal__event-title),
.vuecal-custom :deep(.vuecal__event .vuecal__event-time){
  background: transparent !important;
}

/* Colores de formato (fondo) */
.vuecal-custom :deep(.vuecal__event.format-entero),
.vuecal-custom :deep(.vuecal__event.format-entero .vuecal__event-content),
.vuecal-custom :deep(.vuecal__event.format-entero .vuecal__event-title),
.vuecal-custom :deep(.vuecal__event.format-entero .vuecal__event-time){background:#56B686 !important;}
.vuecal-custom :deep(.vuecal__event.format-harinado),
.vuecal-custom :deep(.vuecal__event.format-harinado .vuecal__event-content),
.vuecal-custom :deep(.vuecal__event.format-harinado .vuecal__event-title),
.vuecal-custom :deep(.vuecal__event.format-harinado .vuecal__event-time){background:#D85796 !important;}
.vuecal-custom :deep(.vuecal__event.format-multiparticle),
.vuecal-custom :deep(.vuecal__event.format-multiparticle .vuecal__event-content),
.vuecal-custom :deep(.vuecal__event.format-multiparticle .vuecal__event-title),
.vuecal-custom :deep(.vuecal__event.format-multiparticle .vuecal__event-time){background:#3076EE !important;}
.vuecal-custom :deep(.vuecal__event.format-pelet_3),
.vuecal-custom :deep(.vuecal__event.format-pelet_3 .vuecal__event-content),
.vuecal-custom :deep(.vuecal__event.format-pelet_3 .vuecal__event-title),
.vuecal-custom :deep(.vuecal__event.format-pelet_3 .vuecal__event-time){background:#E31431 !important;}
.vuecal-custom :deep(.vuecal__event.format-pelet_6),
.vuecal-custom :deep(.vuecal__event.format-pelet_6 .vuecal__event-content),
.vuecal-custom :deep(.vuecal__event.format-pelet_6 .vuecal__event-title),
.vuecal-custom :deep(.vuecal__event.format-pelet_6 .vuecal__event-time){background:#E87B37 !important;}
.vuecal-custom :deep(.vuecal__event.format-pelet_8),
.vuecal-custom :deep(.vuecal__event.format-pelet_8 .vuecal__event-content),
.vuecal-custom :deep(.vuecal__event.format-pelet_8 .vuecal__event-title),
.vuecal-custom :deep(.vuecal__event.format-pelet_8 .vuecal__event-time){background:#845FEF !important;}
.vuecal-custom :deep(.vuecal__event.format-pelet_12),
.vuecal-custom :deep(.vuecal__event.format-pelet_12 .vuecal__event-content),
.vuecal-custom :deep(.vuecal__event.format-pelet_12 .vuecal__event-title),
.vuecal-custom :deep(.vuecal__event.format-pelet_12 .vuecal__event-time){background:#EBA23D !important;}
.vuecal-custom :deep(.vuecal__event.format-other),
.vuecal-custom :deep(.vuecal__event.format-other .vuecal__event-content),
.vuecal-custom :deep(.vuecal__event.format-other .vuecal__event-title),
.vuecal-custom :deep(.vuecal__event.format-other .vuecal__event-time){background:#3076EE !important;color:#ffffff !important;}

/* Nuevo y Modificada se ven grises. El color aparece desde En proceso. */
.vuecal-custom :deep(.vuecal__event.status-new),
.vuecal-custom :deep(.vuecal__event.status-new .vuecal__event-content),
.vuecal-custom :deep(.vuecal__event.status-new .vuecal__event-title),
.vuecal-custom :deep(.vuecal__event.status-new .vuecal__event-time),
.vuecal-custom :deep(.vuecal__event.status-modified),
.vuecal-custom :deep(.vuecal__event.status-modified .vuecal__event-content),
.vuecal-custom :deep(.vuecal__event.status-modified .vuecal__event-title),
.vuecal-custom :deep(.vuecal__event.status-modified .vuecal__event-time){
  background:#E5E7EB !important;
  color:#374151 !important;
  border-color:#D1D5DB !important;
  opacity: 0.7 !important;
}

/* Evitar efecto hover en eventos inactivos */
.vuecal-custom :deep(.vuecal__event.status-new):hover,
.vuecal-custom :deep(.vuecal__event.status-modified):hover{
  transform: none !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12) !important;
}

/* Colores de empaque (borde izquierdo) */
.vuecal-custom :deep(.vuecal__event.packaging-granel)   {border-left:8px solid #020405;}
.vuecal-custom :deep(.vuecal__event.packaging-ensacado) {border-left:8px solid #4CE51E;}
.vuecal-custom :deep(.vuecal__event.packaging-maxisaco) {border-left:8px solid #0CEEE7;}
.vuecal-custom :deep(.vuecal__event.packaging-other)    {border-left:8px solid #020405;}

.vuecal-custom :deep(.vuecal__cell-events-count){ display:none; }

.vuecal-custom :deep(.vuecal__event-title){
  font-size:9px;
  font-weight:500;
  line-height:1.1;
  white-space:normal;
  overflow:hidden;
  display:block;
  height:100%;
  padding:0;
  margin:0;
}

.vuecal-custom :deep(.event-line-1) {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 1px;
}

.vuecal-custom :deep(.event-line-2) {
  margin-top: 5px;
  /* margin-bottom: 5px; */
  padding-bottom: 5%;
  display: flex;
  justify-content: flex-start;
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vuecal-custom :deep(.event-id) {
  font-weight: 600;
  flex-shrink: 0;
}

.vuecal-custom :deep(.event-weight) {
  font-weight: 600;
  flex-shrink: 0;
}

.vuecal-custom :deep(.event-client) {
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Ocultar la hora por defecto */
.vuecal-custom :deep(.vuecal__event-time){
  display: none !important;
}

.vuecal-custom :deep(.vuecal__event-content){
  display:flex;
  flex-direction:column;
  height:100%;
  justify-content:flex-start;
  padding:1px;
  position: relative;
}

.vuecal-custom :deep(.vuecal__cell-content){ padding:2px; }

.vuecal-custom :deep(.vuecal__event):hover{
  transform:scale(1.02);
  box-shadow:0 2px 6px rgba(0,0,0,0.15);
  z-index:10;
}

/* ---------- modal y panel derecho ---------- */
.modal-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;z-index:2000;}
.modal{background:#fff;border-radius:8px;padding:20px;min-width:300px;max-width:400px;box-shadow:0 4px 12px rgba(0,0,0,.25);text-align:center;}
.modal h3{margin-top:0;color:#2c3e50;margin-bottom:20px;}
.color-input-section{margin-bottom:20px;}
.color-input-section label{display:block;margin-bottom:8px;font-weight:600;color:#2c3e50;}
.color-input-section input[type="color"]{width:60px;height:40px;border:none;border-radius:4px;cursor:pointer;}
.default-colors-section{margin-bottom:20px;}
.default-colors-section label{display:block;margin-bottom:8px;font-weight:600;color:#2c3e50;}
.default-colors{display:flex;justify-content:center;gap:10px;}
.color-button{padding:8px 12px;border:2px solid transparent;border-radius:4px;cursor:pointer;font-weight:600;color:#fff;font-size:12px;transition:all .2s ease;}
.color-button:hover{transform:scale(1.05);border-color:#fff;}
.color-button.active{border-color:#2c3e50;transform:scale(1.1);}
.modal-actions{margin-top:20px;display:flex;justify-content:center;gap:10px;}
.modal-actions button{padding:8px 16px;border:none;border-radius:4px;cursor:pointer;font-weight:600;transition:background-color .2s ease;}
.modal-actions .ok{background:#0B8043;color:#fff;}
.modal-actions .ok:hover{background:#0a6b37;}
.modal-actions .cancel{background:#e74c3c;color:#fff;}
.modal-actions .cancel:hover{background:#c0392b;}
.modal-actions .default-btn{background:#3498db;color:#fff;}
.modal-actions .default-btn:hover{background:#2980b9;}

/* Panel derecho */
.event-info-modal{max-width:500px;text-align:left;max-height:80vh;display:flex;flex-direction:column;overflow:hidden;}
.event-info-modal .event-details{max-height:calc(80vh - 120px);overflow-y:auto;padding-right:8px;}
.ingredients-list{max-height:220px;overflow-y:auto;padding-right:8px;}
.ingredients-list::-webkit-scrollbar{width:8px;}
.ingredients-list::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.12);border-radius:4px;}
.ingredients-list::-webkit-scrollbar-track{background:transparent;}
.notes-section{margin-top:15px;border-top:2px solid #e9ecef;padding-top:15px;}
.notes-header{border-bottom:1px solid #dee2e6;margin-bottom:10px;padding-bottom:8px;}
.notes-header label{font-weight:bold;font-size:14px;color:#495057;}
.notes-input-container{padding-left:10px;}
.notes-textarea{width:100%;border:1px solid #dee2e6;border-radius:4px;padding:8px;font-size:13px;font-family:'Source Sans Pro',sans-serif;color:#495057;resize:vertical;min-height:60px;}
.notes-textarea:focus{outline:none;border-color:#80bdff;box-shadow:0 0 0 0.2rem rgba(0,123,255,0.25);}

/* ---------- botón volver a admin ---------- */
.return-admin-btn{margin-top:10px;padding:8px 16px;background:#4e4e51;color:#fff;border:1px solid #4e4e51;border-radius:0;cursor:pointer;font-weight:normal;font-size:13px;text-transform:uppercase;font-family:'Source Sans Pro',sans-serif;transition:background-color .2s ease;}
.return-admin-btn:hover{background:#3a3a3d;border-color:#3a3a3d;}

/* Suma de toneladas */
.tonnage-summary{margin-top:2rem;padding:1rem;background:#fff;border:1px solid #d3d8dc;border-radius:0;position:sticky;top:0;z-index:10;width:270px;}
.tonnage-summary h4{margin:0 0 0.75rem 0;font-size:1em;font-weight:bold;color:#4e4e51;text-transform:uppercase;letter-spacing:0.5px;position:sticky;}
.tonnage-display{text-align:center;}
.tonnage-value{display:block;font-size:1.5rem;font-weight:bold;color:#28a745;margin-bottom:0.25rem;}
.tonnage-count{color:#6c757d;font-size:0.8rem;}

/* ---------- Controles del calendario ---------- */
.vuecal-custom :deep(.vuecal__header) {
  position: relative !important;
  padding: 4px 8px !important; /* reduce header height */
  min-height: 36px !important;
}

.vuecal-custom :deep(.vuecal__view-btn) {
  background: #fff !important;
  border: 1px solid #ddd !important;
  border-radius: 4px !important;
  color: #666 !important;
  font-family: 'Source Sans Pro', sans-serif !important;
  font-size: 13px !important;
  font-weight: normal !important;
  padding: 4px 10px !important; /* tighter */
  margin: 0 2px !important;
  text-transform: none !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
  text-shadow: none !important; /* remove text shadow */
}
.vuecal-custom :deep(.vuecal__view-btn):hover {
  background: #f5f5f5 !important;
  border-color: #ccc !important;
  color: #333 !important;
}
.vuecal-custom :deep(.vuecal__view-btn.vuecal__view-btn--active) {
  background: #4e4e51 !important;
  border-color: #4e4e51 !important;
  color: #fff !important;
  box-shadow: none !important;
  text-shadow: none !important;
}

/* Botón "Hoy" */
.vuecal-custom :deep(.vuecal__today-btn) {
  background: #fff !important;
  border: 1px solid #ddd !important;
  border-radius: 4px !important;
  color: #666 !important;
  font-family: 'Source Sans Pro', sans-serif !important;
  font-size: 13px !important;
  font-weight: normal !important;
  padding: 4px 10px !important;
  margin: 0 8px !important; /* no auto margin; layout handled by flex */
  text-transform: none !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
  text-shadow: none !important;
  position: relative !important;
  right: auto !important;
  top: auto !important;
  transform: none !important;
  z-index: 50 !important;
  pointer-events: auto !important;
  cursor: pointer !important;
}
.vuecal-custom :deep(.vuecal__today-btn):hover {
  background: #f5f5f5 !important;
  border-color: #ccc !important;
  color: #333 !important;
}

.vuecal-custom :deep(.calendar-nav-wrapper) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
}

.vuecal-custom :deep(.calendar-nav__today) {
  margin: 0 12px !important;
  flex-shrink: 0;
}

.vuecal-custom :deep(.vuecal__title-bar) {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important; /* spread left and right groups */
  gap: 12px !important;
}

/* Flechas de navegación */
.vuecal-custom :deep(.vuecal__arrow) {
  display: inline-flex !important;
  justify-content: center !important;
  align-items: center !important;
  background: #fff !important;
  color: #666 !important;
  border: 1px solid #ddd !important;
  border-radius: 4px !important;
  font-size: 14px !important;
  height: 28px !important;
  width: 28px !important;
  margin: 0 4px !important;
  line-height: 1 !important;
  padding: 0 !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
  text-shadow: none !important;
  text-align: center !important;
}

.vuecal-custom :deep(.vuecal__arrow):hover {
  background: #f5f5f5 !important;
  border-color: #ccc !important;
  color: #333 !important;
}

.vuecal-custom :deep(.calendar-day-nav__btn) {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background: #fff;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  height: 28px;
  width: 28px;
  margin: 0 4px;
  line-height: 1;
  padding: 0;
  box-shadow: none;
  transition: all 0.2s ease;
  text-shadow: none;
  text-align: center;
  cursor: pointer;
}
.vuecal-custom :deep(.calendar-day-nav__btn:hover) {
  background: #f5f5f5;
  border-color: #ccc;
  color: #333;
}
.vuecal-custom :deep(.calendar-nav-group) {
  display: flex;
  align-items: center;
  gap: 6px !important;
}
.vuecal-custom :deep(.calendar-nav-group--left) {
  /* flex: 1; */
  justify-content: flex-start;
  /* margin-right: 12px; */
}
.vuecal-custom :deep(.calendar-nav-group--right) {
  flex: 1;
  justify-content: flex-end;
  margin-left: 12px;
}

/* Título del mes/semana en el header */
.vuecal-custom.production :deep(.vuecal__title) {
  color: #666 !important;
  font-family: 'Source Sans Pro', sans-serif !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  position: absolute !important;
  justify-content: space-between !important;
  display: flex !important;
  align-items: center !important;
  left: 33% !important;
  margin: 0 !important;
  text-shadow: none !important;
  z-index: 10 !important;
  pointer-events: none !important;
}

/* Botones dentro del título en rojo */
.vuecal-custom :deep(.vuecal__title button) {
  background: #e74c3c !important;
  border: 1px solid #e74c3c !important;
  color: #fff !important;
  border-radius: 4px;
  padding: 3px 8px; /* slightly tighter */
  font-size: 13px;
  cursor: pointer;
  box-shadow: none !important;
  text-shadow: none !important;
  pointer-events: auto !important; /* Re-enable pointer events for buttons inside title */
}
.vuecal-custom :deep(.vuecal__title button:hover) {
  background: #c0392b !important;
  border-color: #c0392b !important;
}
</style>
